import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { useUserActionSendMessage } from '../../chatbot/hooks/userActions';
import { TRootState } from '../../common/reducers';
import { chatActions } from '../../common/reducers/chat';
import { selectConversationId } from '../../common/selectors/chat';
import { isAnonymousAiChatbotEnabled } from '../../util/isAnonymousAiChatbotEnabled';
import { renderModel } from '../../util/renderModel';

const readUrlParam = (name: string): string | null => new URLSearchParams(window.location.search).get(name);

const readReferralFromUrl = (): string => readUrlParam('referral') || 'Contact';

const removeFromUrl = (names: string[]) => {
  const searchParams = new URLSearchParams(window.location.search);
  names.forEach((name) => searchParams.delete(name));
  const newSearch = searchParams.toString();
  const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
  window.history.replaceState(null, '', newUrl);
};

export const useInitialQueryFromUrl = () => {
  const dispatch = useDispatch();
  const store = useStore<TRootState>();
  const sendMessage = useUserActionSendMessage();
  const conversationId = useSelector(selectConversationId);
  const [query] = useState(() => readUrlParam('query'));
  // `?flow=<start-skill flow>` starts the chat in that flow's skill (`START_SKILL_FLOWS` on the server).
  const [flow] = useState(() => readUrlParam('flow'));
  const hasSent = useRef(false);

  useEffect(() => {
    if (query || flow) {
      removeFromUrl(['query', 'flow']);
      dispatch(
        chatActions.setStartChatParameters({
          ...store.getState().chat.startChatParameters,
          // A flow alone never replaces an ongoing conversation; the server decides (`shouldStartNewChat`).
          ...(query && { shouldStartNewChat: 'true' }),
          referral: readReferralFromUrl(),
          ...(flow && { flow }),
        }),
      );
    }
  }, [dispatch, store, query, flow]);

  useEffect(() => {
    if (!query || !conversationId || hasSent.current) {
      return;
    }
    if (!renderModel.getUserId() && !isAnonymousAiChatbotEnabled()) {
      return;
    }
    hasSent.current = true;
    dispatch(chatActions.setStartChatParameters(undefined));
    dispatch(chatActions.setReloadStartChat(false));
    sendMessage({
      value: query,
      action_type: 'enter',
      attachments: [],
      isLiveChat: false,
    });
  }, [query, conversationId, sendMessage, dispatch]);
};
